import { useRecoilState } from "recoil"
import { useState } from "react"

import { dealerInfo } from "../../atoms/DealerAtom"
import {
  FinalWhereClsCBSale,
  filtersValuesStateCB,
  FinalWhereClsCBService,
  recordCountNumberCB,
} from "../../atoms/CustomerBuilderAtom"
import {
  bigQueryURL,
  resolveDealerVaultSqlClauses,
} from "../../util/bigQueryURL"
import useAuth from "../../Hooks/useAuth"
import { postApiHelper } from "../../util/apiHelpers"
import CampaignModal from "./CampaignModalSimple"

const RECORD_COUNT_LIMIT = 1000000

const downloadBlobResponse = (response, fileName) => {
  if (!response?.data || response.status !== 200) {
    return false
  }
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement("a")
  link.href = blobUrl
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
  return true
}

function DownloadResults() {
  const [SalesFinalWhereCls] = useRecoilState(FinalWhereClsCBSale)
  const [ServiceFinalWhereCls] = useRecoilState(FinalWhereClsCBService)
  const dealer = useRecoilState(dealerInfo)[0]
  const {
    neverPurchased,
    nevSerPrevPurch,
    zipCodesCount,
    states,
    nevSerDateRange,
    nevSerDateRangePrevSer,
  } = useRecoilState(filtersValuesStateCB)[0]
  const [openCampaignModal, setOpenCampaignModal] = useState(false)
  const [campaignModalError, setCampaignModalError] = useState("")
  const [facebookSpinner, setFacebookSpinner] = useState(false)
  const [allDataSpinner, setAllDataSpinner] = useState(false)
  const [recordCount] = useRecoilState(recordCountNumberCB)
  const [fileName, setFileName] = useState(
    `Customer-Builder-${new Date().toLocaleDateString()}.csv`
  )
  const [showNamingFile, setShowNamingFile] = useState(false)
  const authPermRols = useAuth(
    [""],
    false,
    ["super-admin", "admin", "Management", "Staff", "Business"],
    true
  )

  const changeFileName = (e) => {
    setShowNamingFile(true)
  }

  const buildSqlPayload = () => {
    const geoExpanded =
      (ServiceFinalWhereCls.sql.includes(" AND 1=0") &&
        SalesFinalWhereCls.sql.includes(" AND 1=0")) ||
      zipCodesCount ||
      states?.length

    const serviceInput = geoExpanded
      ? ServiceFinalWhereCls.sql.replace(" AND 1=0", "")
      : ServiceFinalWhereCls.sql
    const salesInput = geoExpanded
      ? SalesFinalWhereCls.sql.replace(" AND 1=0", "")
      : SalesFinalWhereCls.sql

    return resolveDealerVaultSqlClauses(serviceInput, salesInput, {
      neverPurchased,
      nevSerPrevPurch,
    })
  }

  const csvDataDownload = () => {
    const url = bigQueryURL(
      nevSerDateRangePrevSer,
      neverPurchased,
      nevSerPrevPurch,
      nevSerDateRange
    ).csv
    const { sqlService, sqlSales } = buildSqlPayload()
    setShowNamingFile(false)
    setFacebookSpinner(true)
    const dealerIDLog = JSON.parse(
      localStorage.getItem("permissionsDealerOMG")
    ).userID
    postApiHelper(
      `BigQuery/${url}`,
      {
        sqlService,
        sqlSales,
        roofTopID: dealer.rooftopID,
        dealerId: dealer.dealerID,
        fileName: `${fileName}.csv`,
        userId: dealerIDLog,
      },
      { responseType: "blob" }
    ).then((response) => {
      const ct = recordCount.value ? `ct${recordCount.value}` : ""
      const date = new Date().toLocaleDateString().split("/")
      const formattedDate = `_${date[0]}-${date[1]}-${date[2].slice(2)}`
      const fileNameNew = `${fileName}-${ct}${formattedDate}.csv`
      downloadBlobResponse(response, fileNameNew)
      setFacebookSpinner(false)
    })
  }

  const downloadAllData = () => {
    const date = new Date().toLocaleDateString().split("/")
    const formattedDate = `_${date[0]}-${date[1]}-${date[2].slice(2)}`
    const allDataFileName = `Customer-Builder-All-Data${formattedDate}`
    const dataEndpoint = bigQueryURL(
      nevSerDateRangePrevSer,
      neverPurchased,
      nevSerPrevPurch,
      nevSerDateRange
    ).csv
    const { sqlService, sqlSales } = buildSqlPayload()
    setAllDataSpinner(true)
    const dealerIDLog = JSON.parse(
      localStorage.getItem("permissionsDealerOMG")
    ).userID
    postApiHelper(
      `BigQuery/getConsumersRawListCSV`,
      {
        sqlService,
        sqlSales,
        roofTopID: dealer.rooftopID,
        dealerId: dealer.dealerID,
        dataEndpoint,
        fileName: `${allDataFileName}.csv`,
        userId: dealerIDLog,
      },
      { responseType: "blob" }
    )
      .then((response) => {
        const ct = recordCount.value ? `ct${recordCount.value}` : ""
        const fileNameNew = `${allDataFileName}-${ct}${formattedDate}.csv`
        downloadBlobResponse(response, fileNameNew)
        setAllDataSpinner(false)
      })
      .catch(() => setAllDataSpinner(false))
  }

  const handleShowModal = () => {
    if (!dealer.fbaDaccountID && !dealer.fb_page_id) {
      setCampaignModalError("Facebook Info is missing")
      return
    }
    if (
      (!recordCount.value && recordCount.value !== 0) ||
      recordCount.value > RECORD_COUNT_LIMIT
    ) {
      setCampaignModalError("Audience must be 1,000,000 or less")
      return
    }
    setCampaignModalError("")
    setOpenCampaignModal(true)
  }

  return (
    <>
      {openCampaignModal && (
        <CampaignModal
          closeModal={() => setOpenCampaignModal(false)}
          dealerId={dealer.dealerID}
          recordCount={recordCount}
        />
      )}
      {authPermRols[2] ? (
        <div className="flex flex-col mt-4 bg-[#48578F] p-[20px] rounded-[12px]">
          <h1 className="font-bold mb-2 text-[#FFFFFF]">
            CSV Customer Download
          </h1>
          <button
            type="button"
            className="mb-4 text-white bg-[#298FC2] rounded-[12px] hover:bg-blue-800  px-5 py-2.5 text-center inline-flex items-center mr-2 space-x-4"
            onClick={handleShowModal}
          >
            <>
              <svg
                data-accordion-icon
                className="w-6 h-6 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"
                ></path>
              </svg>
              <p>Send to Facebook</p>
            </>
          </button>
          {campaignModalError && (
            <p className="text-red-600 font-bold mt-[-10px] pb-2">
              campaignModalError
            </p>
          )}
          <button
            type="button"
            className="mb-4 text-white bg-[#298FC2] rounded-[12px] hover:bg-blue-800  px-5 py-2.5 text-center inline-flex items-center mr-2 space-x-4"
            onClick={changeFileName}
          >
            {facebookSpinner ? (
              <>
                <svg
                  role="status"
                  className="inline mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-[#586283]"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="text-[14px] font-normal">
                  Generating File..
                </span>
              </>
            ) : (
              <>
                <svg
                  data-accordion-icon
                  className="w-6 h-6 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"
                  ></path>
                </svg>
                <p>Facebook</p>
              </>
            )}
          </button>
          <button
            type="button"
            className="mb-4 text-white bg-[#298FC2] rounded-[12px] hover:bg-blue-800  px-5 py-2.5 text-center inline-flex items-center mr-2 space-x-4"
          >
            <svg
              data-accordion-icon
              className="w-6 h-6 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"
              ></path>
            </svg>
            <p>Amazon</p>
          </button>
          <button
            type="button"
            className="mv-4 text-white bg-[#298FC2]  rounded-[12px] hover:bg-blue-800  px-5 py-2.5 text-center inline-flex items-center mr-2 space-x-4 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={downloadAllData}
            disabled={allDataSpinner}
          >
            {allDataSpinner ? (
              <>
                <svg
                  role="status"
                  className="inline mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-[#586283]"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="text-[14px] font-normal">Downloading...</span>
              </>
            ) : (
              <>
                <svg
                  data-accordion-icon
                  className="w-6 h-6 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"
                  ></path>
                </svg>
                <p>All Data</p>
              </>
            )}
          </button>
        </div>
      ) : null}
      {showNamingFile ? (
        <div className="flex flex-col space-y-4 mb-2 mt-2 bg-[#48578F] p-[20px] rounded-[12px]">
          <input
            onChange={(e) => setFileName(e.target.value)}
            placeholder="File Name"
            className="rounded-xl p-[16px]  focus:outline-[#58628325]"
          />
          <button
            onClick={csvDataDownload}
            className="flex items-center w-full justify-around text-white bg-[#298FC2] focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2"
          >
            Download CSV
          </button>
        </div>
      ) : null}
    </>
  )
}

export default DownloadResults
